/**
 * TrainerVisibilityRequestsModule
 * Admin panelinde antrenör liste görünürlüğü taleplerini yönetir
 */
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  EyeOff, 
  UserCheck,
  UserX,
  Star,
  Mail,
  Phone,
  Calendar,
  Dumbbell,
  Briefcase,
  Building2
} from "lucide-react";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrainerVisibilityRequestsModule({ onUpdate }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeTab, setActiveTab] = useState("pending");
  
  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Remove modal
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removingTrainer, setRemovingTrainer] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  
  // Preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTrainer, setPreviewTrainer] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API}/api/admin/trainer-visibility-requests`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
        setPendingCount(data.pending_count || 0);
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (requestId) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/trainer-visibility-requests/${requestId}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success(t("admin:toast_talepOnaylandiAntrenorArtikListede"));
        fetchRequests();
      } else {
        const error = await res.json();
        toast.error(error.detail || t("admin:approveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Red nedeni zorunludur");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/trainer-visibility-requests/${rejectingRequest.request_id}/reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectReason })
      });
      
      if (res.ok) {
        toast.success(t('common:requestRejected'));
        setShowRejectModal(false);
        setRejectingRequest(null);
        setRejectReason("");
        fetchRequests();
      } else {
        const error = await res.json();
        toast.error(error.detail || t("admin:rejectFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveFromList = async () => {
    if (!removeReason.trim()) {
      toast.error(t("common:toast_kaldirmaNedeniZorunludur"));
      return;
    }
    
    setSubmitting(true);
    try {
      // Use user_id for freelance trainers, trainer_id for business trainers
      const id = removingTrainer.user_id || removingTrainer.trainer_id;
      const res = await fetch(`${API}/api/admin/trainers/${id}/remove-from-list`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: removeReason })
      });
      
      if (res.ok) {
        toast.success(t("common:toast_antrenorListedenKaldirildi"));
        setShowRemoveModal(false);
        setRemovingTrainer(null);
        setRemoveReason("");
        fetchRequests();
      } else {
        const error = await res.json();
        toast.error(error.detail || t("common:removeFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch trainer details for preview
  const handlePreview = async (request) => {
    setLoadingPreview(true);
    setShowPreviewModal(true);
    try {
      const userId = request.user_id || request.trainer_id;
      const res = await fetch(`${API}/api/auth/user/${userId}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewTrainer({ ...data, request });
      } else {
        // If user endpoint fails, show basic info from request
        setPreviewTrainer({ 
          name: request.trainer_name?.split(' ')[0] || '',
          surname: request.trainer_name?.split(' ').slice(1).join(' ') || '',
          request 
        });
      }
    } catch (error) {
      console.error("Error fetching trainer:", error);
      setPreviewTrainer({ 
        name: request.trainer_name?.split(' ')[0] || '',
        surname: request.trainer_name?.split(' ').slice(1).join(' ') || '',
        request 
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Beklemede</Badge>;
      case "approved":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Onaylandı</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Reddedildi</Badge>;
      case "removed":
        return <Badge className="bg-slate-100 text-slate-700"><EyeOff className="w-3 h-3 mr-1" /> Kaldırıldı</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === "pending") return r.status === "pending";
    if (activeTab === "approved") return r.status === "approved";
    if (activeTab === "rejected") return r.status === "rejected" || r.status === "removed";
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">{t("common:antrenorListeTalepleri")}</h2>
          <p className="text-grey text-sm">{t("common:antrenorlerinAnaSayfadaGorunmeTaleplerin")}</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-amber-500 text-white text-lg px-3 py-1">
            {pendingCount} Bekleyen Talep
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="w-4 h-4" />
            Bekleyen
            {pendingCount > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white">{pendingCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <Eye className="w-4 h-4" />
            Onaylanan
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-2">
            <EyeOff className="w-4 h-4" />
            Reddedilen / Kaldırılan
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredRequests.length === 0 ? (
            <Card className="rounded-2xl">
              <CardContent className="p-8 text-center">
                <UserCheck className="w-12 h-12 mx-auto text-grey/30 mb-4" />
                <h3 className="text-lg font-semibold text-grey">
                  {activeTab === "pending" ? "Bekleyen talep yok" : 
                   activeTab === "approved" ? "Onaylanan talep yok" : 
                   t("admin:noRejectedRequests")}
                </h3>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common:trainer')}</TableHead>
                    <TableHead>{t("admin:kvkkOnayi")}</TableHead>
                    <TableHead>Talep Tarihi</TableHead>
                    <TableHead>{t('common:status')}</TableHead>
                    <TableHead className="text-right">{t('common:actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.request_id}>
                      <TableCell>
                        <div className="font-medium text-navy">{request.trainer_name}</div>
                      </TableCell>
                      <TableCell>
                        {request.kvkk_consent ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {request.kvkk_consent_date ? format(new Date(request.kvkk_consent_date), "dd/MM/yyyy HH:mm", { locale: getDateLocale() }) : t('admin:approved')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">{t('common:notAvailable')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(request.created_at), "dd/MM/yyyy HH:mm", { locale: getDateLocale() })}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(request.status)}
                        {request.rejection_reason && (
                          <p className="text-xs text-red-600 mt-1">Neden: {request.rejection_reason}</p>
                        )}
                        {request.removal_reason && (
                          <p className="text-xs text-slate-600 mt-1">Neden: {request.removal_reason}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Preview Button - Always visible */}
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => handlePreview(request)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ön İzleme
                          </Button>
                          
                          {request.status === "pending" && (
                            <>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApprove(request.request_id)}
                                disabled={submitting}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Onayla
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => {
                                  setRejectingRequest(request);
                                  setShowRejectModal(true);
                                }}
                                disabled={submitting}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reddet
                              </Button>
                            </>
                          )}
                          {request.status === "approved" && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                setRemovingTrainer(request);
                                setShowRemoveModal(true);
                              }}
                            >
                              <EyeOff className="w-4 h-4 mr-1" />
                              Listeden Kaldır
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Talebi Reddet</DialogTitle>
            <DialogDescription>
              {rejectingRequest?.trainer_name} adlı antrenörün liste görünürlük talebini reddetmek üzeresiniz.
              Lütfen red nedenini belirtin.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Red nedeni..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>{t('common:cancel')}</Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={submitting || !rejectReason.trim()}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Modal */}
      <Dialog open={showRemoveModal} onOpenChange={setShowRemoveModal}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("common:listedenKaldir")}</DialogTitle>
            <DialogDescription>
              {removingTrainer?.trainer_name} adlı antrenörü listeden kaldırmak üzeresiniz.
              Antrenör tekrar talep göndermek zorunda kalacak.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t("common:ph_kaldirmaNedeni")}
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveModal(false)}>{t('common:cancel')}</Button>
            <Button 
              variant="destructive" 
              onClick={handleRemoveFromList}
              disabled={submitting || !removeReason.trim()}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Kaldır
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("common:antrenorDetaylari")}</DialogTitle>
            <DialogDescription>
              {previewTrainer?.request?.trainer_name} - Profil Ön İzleme
            </DialogDescription>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-navy" />
            </div>
          ) : previewTrainer ? (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="flex items-start gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={previewTrainer.picture} />
                  <AvatarFallback className="bg-navy/10 text-navy text-xl">
                    {previewTrainer.name?.charAt(0)}{previewTrainer.surname?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-navy">
                    {previewTrainer.name} {previewTrainer.surname}
                  </h3>
                  {previewTrainer.rating && (
                    <div className="flex items-center gap-1 text-amber-500 mt-1">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="font-medium">{previewTrainer.rating?.toFixed(1) || "5.0"}</span>
                      <span className="text-grey text-sm">({previewTrainer.rating_count || 0} değerlendirme)</span>
                    </div>
                  )}
                  {previewTrainer.request?.kvkk_consent && (
                    <Badge className="bg-green-100 text-green-700 mt-2">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      KVKK Onayı: {previewTrainer.request.kvkk_consent_date 
                        ? format(new Date(previewTrainer.request.kvkk_consent_date), "dd/MM/yyyy HH:mm", { locale: getDateLocale() }) 
                        : t("admin:approved")}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                {previewTrainer.email && (
                  <div className="flex items-center gap-2 text-grey">
                    <Mail className="w-4 h-4" />
                    <span>{previewTrainer.email}</span>
                  </div>
                )}
                {previewTrainer.phone && (
                  <div className="flex items-center gap-2 text-grey">
                    <Phone className="w-4 h-4" />
                    <span>{previewTrainer.phone}</span>
                  </div>
                )}
              </div>

              {/* Bio */}
              {previewTrainer.bio && (
                <div>
                  <h4 className="font-semibold text-navy mb-2">{t('business:about')}</h4>
                  <p className="text-grey text-sm">{previewTrainer.bio}</p>
                </div>
              )}

              {/* Known Branches - Bilinen Branşlar */}
              {previewTrainer.known_branches?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                    <Dumbbell className="w-4 h-4" />
                    Bildiği Branşlar
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {previewTrainer.known_branches.map((branch, i) => (
                      <Badge key={i} variant="secondary">{branch}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Specializations - Uzmanlık Alanları (if different from branches) */}
              {previewTrainer.specializations?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Uzmanlık Alanları
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {previewTrainer.specializations.map((spec, i) => (
                      <Badge key={i} className="bg-amber-100 text-amber-700">{spec}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              <div className="grid grid-cols-2 gap-4">
                {previewTrainer.experience_years && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-grey" />
                    <span className="text-grey">{previewTrainer.experience_years} yıl deneyim</span>
                  </div>
                )}
                {previewTrainer.career_start_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-grey" />
                    <span className="text-grey">
                      Kariyer başlangıcı: {format(new Date(previewTrainer.career_start_date), "MMMM yyyy", { locale: getDateLocale() })}
                    </span>
                  </div>
                )}
              </div>

              {/* Works At Businesses - Çalıştığı İşletmeler */}
              {previewTrainer.works_at_businesses?.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-navy flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Çalıştığı İşletmeler
                  </h4>
                  <div className="space-y-1">
                    {previewTrainer.works_at_businesses.map((biz, i) => (
                      <div key={i} className="text-sm text-grey">
                        • {biz.name} {biz.district && biz.city && `(${biz.district}, ${biz.city})`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Own Businesses - Kendi İşletmeleri */}
              {previewTrainer.own_businesses?.length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-navy flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Kendi İşletmeleri
                  </h4>
                  <div className="space-y-1">
                    {previewTrainer.own_businesses.map((biz, i) => (
                      <div key={i} className="text-sm text-grey">
                        • {biz.name} {biz.district && biz.city && `(${biz.district}, ${biz.city})`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No business info */}
              {(!previewTrainer.works_at_businesses || previewTrainer.works_at_businesses.length === 0) && 
               (!previewTrainer.own_businesses || previewTrainer.own_businesses.length === 0) && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-semibold text-navy flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Serbest Çalışan
                  </h4>
                  <p className="text-sm text-grey mt-1">{t("common:herhangiBirIsletmeyeBagliDegil")}</p>
                </div>
              )}

              {/* Request Status */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-grey text-sm">
                    Talep tarihi: {previewTrainer.request?.created_at 
                      ? format(new Date(previewTrainer.request.created_at), "dd MMMM yyyy, HH:mm", { locale: getDateLocale() })
                      : "-"}
                  </span>
                  {getStatusBadge(previewTrainer.request?.status)}
                </div>
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>{t('common:close')}</Button>
            {previewTrainer?.request?.status === "pending" && (
              <>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    handleApprove(previewTrainer.request.request_id);
                    setShowPreviewModal(false);
                  }}
                  disabled={submitting}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Onayla
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setRejectingRequest(previewTrainer.request);
                    setShowPreviewModal(false);
                    setShowRejectModal(true);
                  }}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reddet
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
